import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { switchAccount } from '../../redux/Slices/userSlice';
import { config } from '../../config';
import accountSwitchingService from '../../services/accountSwitchingService';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import './scss/AccountSwitcher.scss';
import Close from '../icons/Close';
import { formatLastActive } from '../../utils/helpers';

const AccountSwitcher = ({ onClose }) => {
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switchingAccountId, setSwitchingAccountId] = useState(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = () => {
    const storedAccounts = accountSwitchingService.getAccounts();
    const currentAccount = {
      id: user?.user?._id,
      username: user?.user?.username,
      email: user?.user?.email,
      name: user?.user?.name,
      profilePicture: user?.user?.profilePicture,
      verified: user?.user?.verified,
      isActive: true,
      lastActive: new Date().toISOString()
    };

    // Add current account if not already in stored accounts
    if (currentAccount.id && !storedAccounts.find(acc => acc.id === currentAccount.id)) {
      accountSwitchingService.addAccount(user.user);
    }

    // Update current account as active
    accountSwitchingService.setActiveAccountId(currentAccount.id);
    accountSwitchingService.updateLastActive(currentAccount.id);

    // Get updated accounts list
    const updatedAccounts = accountSwitchingService.getAccounts().map(acc => ({
      ...acc,
      isActive: acc.id === currentAccount.id
    }));

    setAccounts(updatedAccounts);
  };

  const handleSwitchAccount = async (targetAccount) => {
    if (targetAccount.isActive || loading) return;

    setSwitchingAccountId(targetAccount.id);
    setLoading(true);

    try {
      // Use Redux action to switch account
      const result = await dispatch(switchAccount(targetAccount.id));

      if (switchAccount.fulfilled.match(result)) {
        // Update account switching service
        accountSwitchingService.setActiveAccountId(targetAccount.id);
        accountSwitchingService.updateLastActive(targetAccount.id);

        // Close the switcher and navigate to home
        onClose();
        navigate('/');
      } else {
        throw new Error(result.payload || 'Failed to switch account');
      }

    } catch (error) {
      console.error('Error switching account:', error);
      // You could add a snackbar notification here
    } finally {
      setLoading(false);
      setSwitchingAccountId(null);
    }
  };

  const handleRemoveAccount = (accountId, event) => {
    event.stopPropagation();

    if (accounts.length <= 1) return; // Don't remove if it's the only account

    accountSwitchingService.removeAccount(accountId);
    loadAccounts();
  };

  const handleAddAccount = async () => {

    try {
      // Store current user data before navigating

      if (user?.user) {
        accountSwitchingService.addAccount(user.user);
        accountSwitchingService.setActiveAccountId(user.user._id);
      }

      // Log out current session to allow new login
      await fetch(config.API_URI + '/v1/users/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });

      onClose();

      // Navigate to login with add account mode
      window.location.href = '/login?mode=add-account';
    } catch (error) {
      console.error('Error preparing for account addition:', error);
      // Fallback: just navigate to login
      onClose();
      window.location.href = '/login?mode=add-account';
    }
  };

  return (
    <>
      <div className="account-switcher-backdrop" onClick={onClose}></div>
      <div className="account-switcher">
        <div className="account-switcher-header">
          <h3>Switch Account</h3>
          <button className="close-button" onClick={onClose}>
            <Close size={24} />
          </button>
        </div>

        <div className="accounts-list">
          {accounts.map((account) => (
            <div
              key={account.id}
              className={`account-item ${account.isActive ? 'active' : ''} ${switchingAccountId === account.id ? 'switching' : ''
                }`}
              onClick={() => handleSwitchAccount(account)}
            >
              <div className="account-avatar">
                {account.profilePicture ? (
                  <img
                    src={account.profilePicture}
                    alt={account.username}
                    className="profile-picture"
                    loading="lazy"
                  />
                ) : (
                  <ImagePlaceholder
                    size={40}
                    member={account}
                    text={account.username}
                    onlineIndicator={false}
                  />
                )}

              </div>

              <div className="account-info">
                <div className="account-name">
                  <span className="username">{account.username}</span>
                  {account.verified && (
                    <span className="verified-badge material-symbols-outlined">verified</span>
                  )}
                </div>
                <div className="account-email">{account.email}</div>
                <div className="last-active">{formatLastActive(account.lastActive)}</div>
              </div>

              <div className="account-actions">
                {switchingAccountId === account.id && (
                  <div className="switching-loader">
                    <span className="material-symbols-outlined spinning">progress_activity</span>
                  </div>
                )}
                {!account.isActive && accounts.length > 1 && (
                  <button
                    className="remove-account"
                    onClick={(e) => handleRemoveAccount(account.id, e)}
                    title="Remove account"
                  >
                    <Close size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="account-switcher-footer">
          <button className="add-account-button" onClick={handleAddAccount}>
            <span className="material-symbols-outlined">add</span>
            Add another account
          </button>
        </div>
      </div>
    </>
  );
};

export default AccountSwitcher;
